import {useCallback, useEffect} from 'react';

import {
  AdCallbacksExtendedFactory,
  AdResponseType,
  AdType,
} from '@shared/Ads/Yandex/types';

import {useAdfoxStore} from '@shared/Ads/Yandex/store';

import {once} from '@shared/Ads/Yandex/utils/once';

import {useAdAnalytics} from '../useAdAnalytics';

export const UNSET_SLOT_ID = 'not_set';

const slotId = UNSET_SLOT_ID;

type Props = {
  adType: AdType;
  position: number | undefined;
  vkId?: number;
  packageName?: string | null;
  logTmrEvent: any;
  emitYmEvent: any;
};

export const useAnalytics = ({adType, position, ...anylytics}: Props) => {
  const logger = useAdAnalytics({platform: 'adfox', adType, ...anylytics});
  const scriptState = useAdfoxStore().adFoxScriptState.state;
  useEffect(() => {
    if (scriptState === 'failed') {
      logger.blocked(UNSET_SLOT_ID, position);
    }
  }, [logger, scriptState, position]);

  return useCallback<AdCallbacksExtendedFactory>(
    ({containerId, cleanupCallbacks}) => {
      logger.requested(slotId, position);
      const prepareShowLogging = (data: AdFox.RenderData) => {
        if (data.stickyAdNode) {
          // флоор_ад -> можно сразу отправлять показ
          logger.shown(slotId, position);
          return;
        }
        // не флоооооор_ад -> цепляем обзервера
        const element = document.getElementById(containerId);
        if (!window.IntersectionObserver || !element) {
          return;
        }
        const observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              logger.shown(slotId, position);
              observer.disconnect();
            }
          },
          {threshold: 0.5},
        );
        observer.observe(element);
        cleanupCallbacks.push(() => observer.disconnect());
      };
      return {
        onClose: () => {
          logger.closed(slotId, position);
        },
        onError: () => {
          logger.blocked(slotId, position);
        },
        onLoad: once(() => {
          logger.gotResponse(slotId, position, {
            AdResponse: AdResponseType.HAS_ADS,
          });
        }),
        onRender: once((data) => {
          logger.rendered(slotId, position);
          prepareShowLogging(data);
        }),
        onStub: once<void>(() => {
          logger.nothingToShow(slotId, position);
          logger.gotResponse(slotId, position, {
            AdResponse: AdResponseType.NO_ADS,
          });
        }),
        onClick: () => {
          logger.clicked(slotId, position);
        },
      };
    },
    [logger, position],
  );
};
